/**
 * Bar number parsing utilities
 * Handles extraction and validation of bar numbers from various formats
 */

import { BAR_NUMBER_PATTERN, ERROR_MESSAGES } from './constants'

/**
 * Extracts bar number from a formatted string like "RL123-4" → 4
 * @param {string} barNumberString - The formatted bar number string
 * @returns {number|null} Extracted bar number or null if invalid
 */
export function extractBarNumber(barNumberString) {
  if (!barNumberString) return null
  
  const match = String(barNumberString).match(BAR_NUMBER_PATTERN)
  if (!match) return null
  
  const barNumber = parseInt(match[1], 10)
  return isNaN(barNumber) ? null : barNumber
}

/**
 * Safely extracts bar number with string conversion
 * Used in contexts where the input might be a number or various string formats
 * @param {string|number} input - The bar number input
 * @returns {string} String representation of bar number or empty string if invalid
 */
export function safeExtractBarNumber(input) {
  const parts = String(input || '').split('-')
  const barNumber = parseInt(parts.pop(), 10)
  return isNaN(barNumber) ? '' : String(barNumber)
}

/**
 * Validates a bar number format
 * @param {string} barNumberString - The formatted bar number string
 * @returns {boolean} True if valid format
 */
export function isValidBarNumberFormat(barNumberString) {
  return BAR_NUMBER_PATTERN.test(String(barNumberString || ''))
}

/**
 * Creates a formatted bar number string
 * @param {string} lotCode - The lot code prefix (e.g., "RL123")
 * @param {number} barNumber - The bar number (e.g., 4)
 * @returns {string} Formatted string (e.g., "RL123-4")
 */
export function formatBarNumber(lotCode, barNumber) {
  if (!lotCode || barNumber == null) {
    throw new Error(ERROR_MESSAGES.INVALID_BAR_NUMBER)
  }
  return `${lotCode}-${barNumber}`
}
